import { Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit, signal, computed } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BulkWorkOrderHeader, SingleEquipmentForm } from '../../../form-model/bulk-workorder-header.form.model';
import { ButtonWithConfirmationComponent } from '../../../shared-components/button-with-confirmation/button-with-confirmation.component';
import { FileUploadComponent } from '../../../shared-components/aems-file-upload/file-upload.component';
import { ApplicationtTextService } from '../../../services/application-text.service';
import { ToastrService } from '../../../services/toastr-service';
import '@maersk-global/mds-components-core/mc-input';
import { Shop } from '../../../models/shop.model';
import { Mode } from '../../../models/mode.model';
import { MasterDataService } from '../../../services/master-data.service';
import { User } from '../../../models/user.model';
import { AttachmentForm } from '../../../form-model/attachment.form.model';
import { BulkWorkOrderSharedDataService } from '../../../services/bulk-work-order-shared-data.service';
import { forkJoin, ReplaySubject, take, takeUntil } from 'rxjs';
import { BulkWorkOrderHeaderValue } from '../../../models/bulk-work-order/bulk-work-order-header.model';
import { ControlValidationService } from '../../../services/control-validation.service';
import { EquipmentDetailsValidator } from './validators/equipment-details.asyncValidator';
import { EquipmentWorkorderExistValidator } from '../../../shared-validators/equipment-workorder-exist.validator';
import { EquipmentMaximumRepairCostValidator } from './validators/equipment-maximum-repair-cost.validator';
import { EquipmentNumberUniqueValidator } from './validators/equipment-unique.validator';
import { ErrorComponent } from '../../../shared-components/error/error.component';
import { CommonModule } from '@angular/common';
import { AemsFile } from '../../../models/aems-file.model';
import { InspectionDetail, InspectionDetailsWithLine } from '../../../models/inpection-detail.model';
import { v4 } from 'uuid';
import { AttachedImage } from '../../../models/maintenance-order-attached-image';
import { utils } from '../../../helper/utils';
import { RepairCodeService } from '../../../services/repair-code.service';

@Component({
  selector: 'app-bulk-work-order-header',
  imports: [ButtonWithConfirmationComponent, CommonModule, ReactiveFormsModule, FileUploadComponent, ErrorComponent],
  templateUrl: './bulk-work-order-header.component.html',
  styleUrl: './bulk-work-order-header.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class BulkWorkOrderHeaderEquipmentsComponent implements OnInit, OnDestroy {
  bulkWorkOrderHeaderForm: FormGroup<BulkWorkOrderHeader>;
  shops: Shop[] = [];
  modes: Mode[] = [];
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  equipmentInspectionDetailsSignal = signal<Map<string, InspectionDetail[]>>(new Map());
  private fetchedEquipmentNumbers = new Set<string>();

  // Computed property for inspection details - this prevents infinite loops
  inspectionDetailsForEquipments = computed(() => {
    const inspectionDetailsMap = this.equipmentInspectionDetailsSignal();
    const equipments = this.equipments?.value || [];

    return equipments.map((equipment, index) => {
      if (!equipment.equipmentNumber) {
        return undefined;
      }

      const inspectionDetails = inspectionDetailsMap.get(equipment.equipmentNumber) || [];
      return {
        isBulkOrderCall: true,
        equipmentNumber: equipment.equipmentNumber,
        inspectionDetails: inspectionDetails,
        isEditMode: false,
        attachReport: true,
      } as InspectionDetailsWithLine;
    });
  });

  constructor(
    private readonly fb: FormBuilder,
    public appTextSvc: ApplicationtTextService,
    private readonly toastr: ToastrService,
    private readonly masterDataSvc: MasterDataService,
    public bulkWorkOrderSharedDataService: BulkWorkOrderSharedDataService,
    public ctrlValidSvc: ControlValidationService,
    private readonly equipmentValidator: EquipmentDetailsValidator,
    private readonly workorderExistValidator: EquipmentWorkorderExistValidator,
    private readonly equipmentMaxRepairCostValidator: EquipmentMaximumRepairCostValidator,
    private readonly equipmentNumberUniquevalidator: EquipmentNumberUniqueValidator,
    private readonly repairCodeService: RepairCodeService,
  ) {
    this.bulkWorkOrderHeaderForm = this.fb.group<BulkWorkOrderHeader>({
      maintenanceShop: this.fb.nonNullable.control({} as Shop),
      maintenanceRepairMode: this.fb.nonNullable.control({} as Mode),
      equipments: this.fb.array<FormGroup<SingleEquipmentForm>>([], {
        validators: [this.equipmentNumberUniquevalidator.validate.bind(this.equipmentNumberUniquevalidator)],
      }),
    });
    this.bulkWorkOrderHeaderForm.valueChanges.subscribe(() => {
      this.postFromValueToSharedService();
    });
    this.bulkWorkOrderHeaderForm.statusChanges.subscribe((status) => {
      this.postFromValueToSharedService();
      this.bulkWorkOrderSharedDataService.HeaderFormStatus = status;
    });
    this.onAddEquipment();
    this.bulkWorkOrderSharedDataService.Reset.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.resetEquipment();
    });
  }

  ngOnDestroy(): void {
    this.fetchedEquipmentNumbers.clear();
    this.resetEquipment();
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.bulkWorkOrderSharedDataService.Mode = null;
    this.bulkWorkOrderSharedDataService.Shop = null;
  }

  private postFromValueToSharedService(): void {
    if (this.bulkWorkOrderHeaderForm.invalid || this.bulkWorkOrderHeaderForm.pending) return;
    const val = this.bulkWorkOrderHeaderForm.getRawValue();
    const fieldsWithEquipmentNumbers = val.equipments.filter((equipment) => equipment.equipmentNumber);
    const headerValue: BulkWorkOrderHeaderValue = {
      maintenanceShopCode: val.maintenanceShop.maintenanceShopCode,
      maintenanceRepairModeCode: val.maintenanceRepairMode.code,
      equipments: fieldsWithEquipmentNumbers.map((equipment) => {
        return {
          equipmentNumber: equipment.equipmentNumber,
          vendorsReference: equipment.vendorsReference,
          attachedImages: equipment.attachedImages.files.map((file) => this.mapAttachments(file)),
          reeferContainerCondition:
            this.bulkWorkOrderSharedDataService.ValdatedEquipmentsSnapShot.find((x) => x.equipmentNumber === equipment.equipmentNumber)
              ?.equipmentTypeCode === 'REEF'
              ? 'NOT_PROVIDED'
              : '',
        };
      }),
    };
    this.bulkWorkOrderSharedDataService.BulkWorkOrderHeader = headerValue;
  }
  private mapAttachments(aemsFile: AemsFile): AttachedImage {
    let file: AttachedImage = {
      name: aemsFile.displayName ?? '',
      path: aemsFile.generatedName,
      extension: aemsFile.generatedName.split('.')[1],
      contentType: aemsFile.file?.type ?? aemsFile.fileType ?? '',
    };

    if (aemsFile.linkFile) {
      file.storageSource = 'eEIR';
      file.storageUrl = aemsFile.fileUrl;
    }

    return file;
  }
  private resetEquipment(): void {
    this.bulkWorkOrderHeaderForm.controls.equipments.clear();
    this.bulkWorkOrderSharedDataService.ClearValidatedEquipments();
    this.fetchedEquipmentNumbers.clear();

    this.onAddEquipment();
  }
  ngOnInit(): void {
    this.bulkWorkOrderHeaderForm.controls.maintenanceShop.valueChanges.subscribe((shop: Shop) => {
      this.bulkWorkOrderSharedDataService.Shop = shop;
      this.resetEquipment();
    });
    this.bulkWorkOrderHeaderForm.controls.maintenanceRepairMode.valueChanges.subscribe((mode: Mode) => {
      this.bulkWorkOrderSharedDataService.Mode = mode;
      this.resetEquipment();
    });

    this.getUserAssignedShop();
    this.loadEnabledModes();

    this.equipments.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.subscribeToEquipmentValidation();
    });
  }

  private loadEnabledModes(): void {
    forkJoin({
      modes: this.masterDataSvc.getRepairModes().pipe(take(1)),
      enabledModeCodes: this.repairCodeService.getAllModes(true).pipe(take(1)),
    })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ modes, enabledModeCodes }) => {
        this.modes = modes.filter((mode) => enabledModeCodes.includes(mode.code));
        if (this.modes.length > 0) {
          this.bulkWorkOrderHeaderForm.controls.maintenanceRepairMode.setValue(this.modes[0]);
        }
      });
  }
  get equipments(): FormArray<FormGroup<SingleEquipmentForm>> {
    return this.bulkWorkOrderHeaderForm.get('equipments') as FormArray<FormGroup<SingleEquipmentForm>>;
  }
  get singleEquipmentForm(): FormGroup<SingleEquipmentForm> {
    const equipmentForm = this.fb.nonNullable.group<SingleEquipmentForm>({
      equipmentNumber: this.fb.nonNullable.control('', {
        updateOn: 'blur',
        validators: [Validators.pattern(/^[A-Z]{4}[0-9]{6,9}$/)],
        asyncValidators: [
          this.equipmentValidator.validate.bind(this.equipmentValidator),
          this.workorderExistValidator.validate.bind(this.workorderExistValidator),
          this.equipmentMaxRepairCostValidator.validate.bind(this.equipmentMaxRepairCostValidator),
        ],
      }),
      vendorsReference: this.fb.nonNullable.control({ value: '', disabled: true }, [
        Validators.maxLength(20),
        Validators.pattern(/^[^,&%]+$/),
      ]),
      attachedImages: this.fb.nonNullable.group<AttachmentForm>({} as AttachmentForm),
    });
    return equipmentForm;
  }
  onAddEquipment(): void {
    this.equipments.push(this.singleEquipmentForm);
  }
  deleteEquipment($index: number): () => void {
    return () => {
      if (this.equipments.length === 1) {
        this.toastr.showToastr(this.appTextSvc.AtleastOneEquipmentRequired, 'warning');
        return;
      }

      const equipmentControl = this.equipments.at($index)?.get('equipmentNumber');
      const equipmentNumber = equipmentControl?.value;
      this.equipments.removeAt($index);
      // if (equipmentNumber) {
      //   const currentMap = new Map(this.equipmentInspectionDetailsSignal());
      //   currentMap.delete(equipmentNumber);
      //   this.equipmentInspectionDetailsSignal.set(currentMap);
      //   this.fetchedEquipmentNumbers.delete(equipmentNumber);
      // }
    };
  }

  onKeyPressLastControlInRow(index: number, vendorRef: FormControl): void {
    vendorRef.enable();
    if (index === this.equipments.length - 1) {
      this.onAddEquipment();
    }
  }

  private getUserAssignedShop() {
    const userData = localStorage.getItem('user');
    if (userData === null) {
      return;
    }
    const user: User = JSON.parse(userData);
    this.shops = user.maintenanceShops.filter((shop) => !shop.isVesselShop);
    if (this.shops.length > 0) {
      this.bulkWorkOrderHeaderForm.controls.maintenanceShop.setValue(this.shops[0]);
    }
  }

  private subscribeToEquipmentValidation(): void {
    this.equipments.controls.forEach((equipmentForm, index) => {
      let previousEquipmentNumber = equipmentForm.controls.equipmentNumber.value;

      equipmentForm.controls.equipmentNumber.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((currentEquipmentNumber) => {
        if (previousEquipmentNumber && previousEquipmentNumber !== currentEquipmentNumber) {
          this.fetchedEquipmentNumbers.delete(previousEquipmentNumber);
          const stillInUse = this.equipments.controls.some(
            (form, idx) => idx !== index && form.controls.equipmentNumber.value === previousEquipmentNumber,
          );
          if (!stillInUse) {
            const currentMap = new Map(this.equipmentInspectionDetailsSignal());
            currentMap.delete(previousEquipmentNumber);
            this.equipmentInspectionDetailsSignal.set(currentMap);
          }
        }
        previousEquipmentNumber = currentEquipmentNumber;
      });

      equipmentForm.controls.equipmentNumber.statusChanges.pipe(takeUntil(this.destroyed$)).subscribe((status) => {
        if (status === 'VALID') {
          const equipmentNumber = equipmentForm.controls.equipmentNumber.value;
          if (equipmentNumber && !this.fetchedEquipmentNumbers.has(equipmentNumber)) {
            this.fetchedEquipmentNumbers.add(equipmentNumber);
            this.fetchEquipmentInspectionDetails(equipmentNumber);
          }
        }
      });
    });
  }

  private fetchEquipmentInspectionDetails(equipmentNumber: string): void {
    const fromDate = new Date();
    fromDate.setDate(new Date().getDate() - 15);
    const fromDateUTC: string = fromDate.toISOString();
    const toDateUTC: string = new Date().toISOString();

    const val = this.bulkWorkOrderHeaderForm.getRawValue();
    let shopCode = val.maintenanceShop.maintenanceShopCode;

    this.masterDataSvc
      .getEquipmentInspectionDetails(shopCode, equipmentNumber, fromDateUTC, toDateUTC)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (inspectionDetails: InspectionDetail[]) => {
          if (inspectionDetails) {
            const detailsWithGuids = this.initializeGuidsForInspectionDetails(inspectionDetails);
            const currentMap = new Map(this.equipmentInspectionDetailsSignal());
            currentMap.set(equipmentNumber, detailsWithGuids);
            this.equipmentInspectionDetailsSignal.set(currentMap);
          } else {
            const currentMap = new Map(this.equipmentInspectionDetailsSignal());
            currentMap.set(equipmentNumber, []);
            this.equipmentInspectionDetailsSignal.set(currentMap);
          }
        },
        error: () => {
          this.fetchedEquipmentNumbers.delete(equipmentNumber);
          const currentMap = new Map(this.equipmentInspectionDetailsSignal());
          currentMap.set(equipmentNumber, []);
          this.equipmentInspectionDetailsSignal.set(currentMap);
        },
      });
  }

  // Simple method for template - uses computed signal to prevent infinite loops
  getInspectionDetailsForEquipmentByIndex(index: number): InspectionDetailsWithLine | undefined {
    const inspectionDetailsArray = this.inspectionDetailsForEquipments();
    let equipmentNumber = this.bulkWorkOrderHeaderForm.controls.equipments.controls[index].value.equipmentNumber;
    return inspectionDetailsArray.find((x) => x?.equipmentNumber === equipmentNumber);
  }

  onInspectionDetailsWithLineAfterUserSelectionDone(event: InspectionDetailsWithLine) {
    if (!event.equipmentNumber) {
      return;
    }

    const currentMap = new Map(this.equipmentInspectionDetailsSignal());
    const existingInspectionDetails = currentMap.get(event.equipmentNumber) || [];

    const updatedInspectionDetails = existingInspectionDetails.map((inspectionDetail) => {
      const eventInspectionDetail = event.inspectionDetails.find(
        (eventDetail) => eventDetail.inspectionId === inspectionDetail.inspectionId,
      );
      if (eventInspectionDetail) {
        const updatedDamageImages = inspectionDetail.damageImages.map((damageImage) => {
          const eventDamageImage = eventInspectionDetail.damageImages.find((eventImage) => eventImage.id === damageImage.id);
          if (eventDamageImage) {
            return {
              ...damageImage,
              isLinked: eventDamageImage.isLinked,
              linkedLineItemIdentifier: eventDamageImage.linkedLineItemIdentifier,
            };
          }
          return damageImage;
        });
        return {
          ...inspectionDetail,
          damageImages: updatedDamageImages,
        };
      }
      return inspectionDetail;
    });
    currentMap.set(event.equipmentNumber, updatedInspectionDetails);
    this.equipmentInspectionDetailsSignal.set(currentMap);
  }

  private initializeGuidsForInspectionDetails(inspectionDetails: InspectionDetail[]): InspectionDetail[] {
    return inspectionDetails.map((detail) => {
      // Create a copy to avoid mutating original data
      const detailWithGuid = { ...detail };

      // Initialize GUID for inspection detail if not already set
      if (!detailWithGuid.guid) {
        detailWithGuid.guid = utils.generateFileNameWithIdAndExtension(v4(), detail.inspectionReportURL);
      }

      // Initialize GUIDs for damage images
      detailWithGuid.damageImages = detail.damageImages.map((image) => {
        const imageWithGuid = { ...image };

        // Initialize GUID for damage image if not already set
        if (!imageWithGuid.guid) {
          imageWithGuid.guid = utils.generateFileNameWithIdAndExtension(v4(), imageWithGuid.path);
        }

        return imageWithGuid;
      });

      return detailWithGuid;
    });
  }
}
